import { useEffect, useMemo, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Plus, Trash, MagnifyingGlass, Tag, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useSettings } from "@/context/SettingsContext";

const emptyForm = { name: "", category: "Tempero", base_price: "", sales_channels: "", notes: "" };

export default function Portfolio() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([api.get("/portfolio"), api.get("/categories")]);
      setItems(pRes.data);
      setCategories(cRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter(
      (it) =>
        (!category || it.category === category) &&
        (!filter || it.name.toLowerCase().includes(filter.toLowerCase())),
    );
  }, [items, filter, category]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim(),
        base_price: parseFloat(form.base_price),
        sales_channels: form.sales_channels.split(",").map((s) => s.trim()).filter(Boolean),
        notes: form.notes.trim() || null,
      };
      await api.post("/portfolio", payload);
      toast.success("Produto adicionado ao portfólio");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/portfolio/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Produto removido");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const anchoredSet = new Set((settings.anchored_channels || []).map((c) => c.toLowerCase()));
  const brand = (settings.brand_name || "").toLowerCase();

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Portfólio Base</div>
          <h1 className="h-display" style={{ color: "var(--text-primary)" }}>
            {settings.brand_name || "Marca de Referência"}
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Itens, preços e canais de venda monitorados como base do comparativo de mercado.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="btn-gold px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2"
          data-testid="add-portfolio-btn"
        >
          <Plus size={14} weight="bold" /> Adicionar item
        </button>
      </header>

      {/* Filters */}
      <div className="surface p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
          <input
            data-testid="portfolio-filter"
            className="input-base pl-9"
            placeholder="Filtrar por nome…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <select
          data-testid="portfolio-category-filter"
          className="input-base md:w-56"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Todas as categorias</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-sm" style={{ color: "var(--text-secondary)" }}>Carregando…</div>
      ) : filtered.length === 0 ? (
        <div className="surface p-8 text-sm" style={{ color: "var(--text-secondary)" }}>
          Nenhum item encontrado.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((it) => (
            <article
              key={it.id}
              data-testid={`portfolio-card-${it.id}`}
              className="surface p-5 surface-hover transition-colors"
            >
              <div className="flex items-start justify-between mb-3 gap-2">
                <span
                  className="text-[10px] uppercase tracking-[0.18em] px-2 py-1"
                  style={{ background: "rgba(212,175,55,0.08)", color: "var(--gold)", border: "1px solid rgba(212,175,55,0.2)" }}
                >
                  {it.category}
                </span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="p-1.5 transition-colors"
                      style={{ color: "var(--text-muted)" }}
                      title="Remover"
                      data-testid={`delete-portfolio-${it.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)", borderRadius: 2 }}>
                    <AlertDialogHeader>
                      <AlertDialogTitle style={{ color: "var(--text-primary)" }}>Remover produto?</AlertDialogTitle>
                      <AlertDialogDescription style={{ color: "var(--text-secondary)" }}>
                        <strong>{it.name}</strong> será removido do portfólio. Essa ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        className="rounded-sm"
                        data-testid={`cancel-delete-${it.id}`}
                        style={{ background: "transparent", border: "1px solid var(--border-default)", color: "var(--text-primary)" }}
                      >
                        Cancelar
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(it.id)}
                        data-testid={`confirm-delete-${it.id}`}
                        className="rounded-sm"
                        style={{ background: "var(--danger)", color: "white", border: "1px solid var(--danger)" }}
                      >
                        Remover
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <h3 className="text-lg font-medium mb-3" style={{ color: "var(--text-primary)" }}>{it.name}</h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-2xl font-light" style={{ color: "var(--gold)" }}>{formatBRL(it.base_price)}</span>
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>preço base</span>
              </div>
              <div>
                <div className="label-eyebrow flex items-center gap-1 mb-2"><Storefront size={11} /> Canais</div>
                <div className="flex flex-wrap gap-1">
                  {(it.sales_channels || []).map((c) => {
                    const isBrand = c.toLowerCase() === brand;
                    const isAnchored = anchoredSet.has(c.toLowerCase());
                    return (
                      <span
                        key={c}
                        className="text-[11px] px-2 py-0.5 inline-flex items-center gap-1"
                        style={{
                          background: isBrand
                            ? "rgba(212,175,55,0.12)"
                            : isAnchored
                              ? "rgba(16,185,129,0.08)"
                              : "var(--bg-surface-hover)",
                          color: isBrand
                            ? "var(--gold)"
                            : isAnchored
                              ? "var(--success)"
                              : "var(--text-secondary)",
                          border: `1px solid ${isBrand ? "rgba(212,175,55,0.3)" : isAnchored ? "rgba(16,185,129,0.25)" : "var(--border-default)"}`,
                        }}
                      >
                        {isBrand && <Tag size={9} weight="fill" />}
                        {c}
                      </span>
                    );
                  })}
                  {(!it.sales_channels || it.sales_channels.length === 0) && (
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span>
                  )}
                </div>
              </div>
              {it.notes && (
                <p className="mt-4 text-xs italic" style={{ color: "var(--text-muted)" }}>{it.notes}</p>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
          onClick={() => setOpen(false)}
        >
          <form
            onSubmit={handleCreate}
            onClick={(e) => e.stopPropagation()}
            className="surface w-full max-w-lg p-6 fade-up"
            data-testid="portfolio-modal"
          >
            <div className="flex items-center gap-2 mb-5">
              <Tag size={18} weight="duotone" style={{ color: "var(--gold)" }} />
              <h2 className="text-xl font-medium" style={{ color: "var(--text-primary)" }}>Novo item do portfólio</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-1">Nome</label>
                <input className="input-base" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow block mb-1">Categoria</label>
                  <input className="input-base" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Preço base (R$)</label>
                  <input className="input-base" required type="number" step="0.01" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="form-price" />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Canais (separados por vírgula)</label>
                <input className="input-base" value={form.sales_channels} onChange={(e) => setForm({ ...form, sales_channels: e.target.value })} placeholder={`${settings.brand_name || "Sabor Dubom"}, Bemol, Mercado Livre`} data-testid="form-channels" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Notas</label>
                <textarea className="input-base" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-wider rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} className="btn-gold px-4 py-2 text-xs uppercase tracking-wider rounded-sm" data-testid="form-submit">
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
